(() => {
  'use strict';

  const storageKey = 'ak-tinson-trade-quote-v1';
  const maxLines = 100;
  const maxQuantity = 99999;
  let toastTimer;
  let lines = [];
  let strings = {};

  try {
    const source = JSON.parse(document.getElementById('trade-strings')?.textContent || '{}');
    strings = source.quote || source;
  } catch {
    strings = {};
  }

  function text(key, values = {}) {
    let value = typeof strings[key] === 'string' ? strings[key] : '';
    Object.entries(values).forEach(([name, replacement]) => {
      value = value.replaceAll(`{{ ${name} }}`, String(replacement)).replaceAll(`{{${name}}}`, String(replacement)).replaceAll(`__${name}__`, String(replacement));
    });
    return value;
  }

  function cleanText(value, limit = 240) {
    return typeof value === 'string' ? value.replace(/[\u0000-\u001f\u007f]/g, ' ').slice(0, limit).trim() : '';
  }

  function productUrl(value) {
    try {
      const url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[^/]+\/?$/i.test(url.pathname)) return '';
      const variant = url.searchParams.get('variant');
      return url.pathname + (variant && /^\d+$/.test(variant) ? `?variant=${variant}` : '');
    } catch {
      return '';
    }
  }

  function imageUrl(value) {
    if (!value) return '';
    try {
      const url = new URL(value, window.location.origin);
      if (url.protocol !== 'https:' && url.origin !== window.location.origin) return '';
      if (url.origin === window.location.origin || url.hostname === 'cdn.shopify.com' || url.hostname.endsWith('.shopifycdn.com')) return url.href;
    } catch {
      return '';
    }
    return '';
  }

  function sanitizeLine(line) {
    if (!line || typeof line !== 'object' || !/^\d{1,24}$/.test(String(line.id))) return null;
    const quantity = Number(line.quantity);
    const title = cleanText(line.title);
    const url = productUrl(line.url);
    if (!title || !url || !Number.isInteger(quantity) || quantity < 1 || quantity > maxQuantity) return null;
    return {
      id: String(line.id),
      title,
      variant: cleanText(line.variant),
      sku: cleanText(line.sku, 120),
      url,
      image: imageUrl(line.image),
      quantity
    };
  }

  function readLines() {
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw || raw.length > 250000) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      const seen = new Set();
      return parsed.slice(0, maxLines).map(sanitizeLine).filter(line => {
        if (!line || seen.has(line.id)) return false;
        seen.add(line.id);
        return true;
      });
    } catch {
      return [];
    }
  }

  function announce(message) {
    const toast = document.getElementById('quote-toast');
    if (!toast) return;
    window.clearTimeout(toastTimer);
    toast.textContent = message;
    toast.hidden = false;
    toastTimer = window.setTimeout(() => { toast.hidden = true; }, 6000);
  }

  function saveLines(nextLines) {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(nextLines));
      lines = nextLines;
      return true;
    } catch {
      announce(text('storage_error'));
      return false;
    }
  }

  function element(tag, className, content) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (content) node.textContent = content;
    return node;
  }

  function summary() {
    if (!lines.length) return text('no_items');
    return lines.map((line, index) => [
      `${index + 1}. ${line.title}`,
      line.variant,
      line.sku ? `${text('sku')}: ${line.sku}` : '',
      `${text('quantity')}: ${line.quantity}`,
      new URL(line.url, window.location.origin).href
    ].filter(Boolean).join('\n')).join('\n\n');
  }

  function updateSummary() {
    const count = lines.reduce((total, line) => total + line.quantity, 0);
    document.querySelectorAll('[data-quote-count]').forEach(node => { node.textContent = String(count); });
    document.querySelectorAll('[data-quote-summary]').forEach(node => { node.value = summary(); });
    document.querySelectorAll('[data-quote-line-count]').forEach(node => {
      node.textContent = text('item_count', { count: lines.length });
    });
    document.querySelectorAll('[data-quote-contact]').forEach(form => {
      const body = form.querySelector('[name="contact[body]"]');
      if (body) body.required = lines.length === 0;
    });
  }

  function renderLines() {
    document.querySelectorAll('[data-quote-items]').forEach(container => {
      container.replaceChildren();
      if (!lines.length) {
        const empty = element('div', 'quote-empty');
        empty.append(element('h3', '', text('empty_title')), element('p', '', text('empty_text')));
        const link = element('a', 'text-link', text('browse'));
        link.href = container.dataset.catalogUrl || '/collections/all';
        empty.append(link);
        container.append(empty);
        return;
      }
      lines.forEach(line => {
        const item = element('article', 'quote-item');
        if (line.image) {
          const image = element('img');
          image.src = line.image;
          image.alt = line.title;
          image.width = 96;
          image.height = 96;
          image.loading = 'lazy';
          item.append(image);
        }
        const info = element('div', 'quote-item-info');
        const title = element('a', '', line.title);
        title.href = line.url;
        const heading = element('h3');
        heading.append(title);
        info.append(heading);
        if (line.variant) info.append(element('p', '', line.variant));
        if (line.sku) info.append(element('small', '', `${text('sku')}: ${line.sku}`));
        item.append(info);
        const actions = element('div', 'quote-item-actions');
        const label = element('label', 'field');
        const labelText = element('span', '', text('quantity'));
        const quantity = element('input');
        quantity.type = 'number';
        quantity.min = '1';
        quantity.max = String(maxQuantity);
        quantity.step = '1';
        quantity.value = String(line.quantity);
        quantity.dataset.quoteQuantity = line.id;
        quantity.setAttribute('aria-label', `${text('quantity')}: ${line.title}, ${line.variant}`);
        label.append(labelText, quantity);
        const remove = element('button', 'text-link', text('remove'));
        remove.type = 'button';
        remove.dataset.quoteRemove = line.id;
        remove.setAttribute('aria-label', `${text('remove')}: ${line.title}, ${line.variant}`);
        actions.append(label, remove);
        item.append(actions);
        container.append(item);
      });
    });
    updateSummary();
  }

  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.matches('[data-quote-add]')) {
      event.preventDefault();
      const selection = form.querySelector('select[name="id"]');
      const option = selection?.selectedOptions[0];
      const quantityInput = form.querySelector('[name="quantity"]');
      const quantity = Number(quantityInput?.value || 1);
      if (!Number.isInteger(quantity) || quantity < 1 || quantity > maxQuantity) {
        announce(text('invalid_quantity'));
        quantityInput?.focus();
        return;
      }
      const line = sanitizeLine({
        id: option?.value,
        title: form.dataset.productTitle,
        variant: option?.dataset.title || option?.textContent,
        sku: option?.dataset.sku || '',
        url: form.dataset.productUrl,
        image: option?.dataset.image || form.dataset.productImage,
        quantity
      });
      if (!line) {
        announce(text('selection_error'));
        return;
      }
      line.url = line.url.split('?')[0] + `?variant=${line.id}`;
      lines = readLines();
      const existing = lines.find(item => item.id === line.id);
      if (existing && existing.quantity + quantity > maxQuantity) {
        announce(text('invalid_quantity'));
        return;
      }
      if (!existing && lines.length >= maxLines) {
        announce(text('basket_full'));
        return;
      }
      const nextLines = existing
        ? lines.map(item => item.id === line.id ? { ...line, quantity: item.quantity + quantity } : item)
        : [...lines, line];
      if (saveLines(nextLines)) {
        renderLines();
        announce(text('added', { product: line.title }));
      }
    } else if (form.matches('[data-quote-contact]')) {
      lines = readLines();
      updateSummary();
      if (!form.checkValidity()) {
        event.preventDefault();
        form.reportValidity();
      }
    }
  });

  document.addEventListener('change', event => {
    const input = event.target;
    if (!(input instanceof HTMLElement)) return;
    if (input.matches('[data-quote-quantity]')) {
      const quantity = Number(input.value);
      const id = input.dataset.quoteQuantity;
      lines = readLines();
      const current = lines.find(line => line.id === id);
      if (!current) return renderLines();
      if (!Number.isInteger(quantity) || quantity < 1 || quantity > maxQuantity) {
        input.value = String(current.quantity);
        announce(text('invalid_quantity'));
        return;
      }
      if (saveLines(lines.map(line => line.id === id ? { ...line, quantity } : line))) updateSummary();
      else input.value = String(current.quantity);
    }
    if (input.matches('[data-quote-add] select[name="id"]')) {
      const image = imageUrl(input.selectedOptions[0]?.dataset.previewImage || input.selectedOptions[0]?.dataset.image);
      const main = document.querySelector('[data-main-image], #product-hero-image');
      if (image && main) {
        main.src = image;
        main.removeAttribute('srcset');
      }
    }
  });

  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    const remove = target?.closest('[data-quote-remove]');
    if (remove) {
      lines = readLines();
      const id = remove.dataset.quoteRemove;
      const removedIndex = lines.findIndex(line => line.id === id);
      if (saveLines(lines.filter(line => line.id !== id))) {
        renderLines();
        const buttons = document.querySelectorAll('[data-quote-remove]');
        const next = buttons[Math.min(removedIndex, buttons.length - 1)] || document.querySelector('[data-quote-items] a');
        next?.focus();
      }
    }
    const thumbnail = target?.closest('[data-image-src]');
    const main = document.querySelector('[data-main-image], #product-hero-image');
    const src = imageUrl(thumbnail?.dataset.imageSrc);
    if (thumbnail && main && src) {
      main.src = src;
      main.removeAttribute('srcset');
      if (thumbnail.dataset.imageAlt) main.alt = thumbnail.dataset.imageAlt;
      document.querySelectorAll('[data-image-src]').forEach(button => button.setAttribute('aria-pressed', String(button === thumbnail)));
    }
  });

  window.addEventListener('storage', event => {
    if (event.key === storageKey || event.key === null) {
      lines = readLines();
      renderLines();
    }
  });

  lines = readLines();
  if (document.querySelector('[data-quote-contact] [data-quote-success]')) {
    try {
      window.localStorage.removeItem(storageKey);
      lines = [];
    } catch {
      announce(text('storage_error'));
    }
  }
  renderLines();
})();
